import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronLeft, FaEdit, FaEye, FaTable, FaTh } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fetchMyInfrastructureRequirementsAPI } from '../../services/infrastructureLeasingListing.service';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/infrastructure_leasing_buyer_requirements.scss';

const LAYOUT_KEY = 'ile_buyer_requirements_layout';

const ASSET_CLASS_OPTIONS = [
  { id: 'commercial_property', label: 'Commercial Property' },
  { id: 'warehouse', label: 'Warehouse' },
  { id: 'factory_industrial', label: 'Factory / Industrial Land' },
  { id: 'industrial_shed', label: 'Industrial Shed' },
  { id: 'machinery', label: 'Machinery' },
  { id: 'equipment_infra', label: 'Equipment & Infrastructure' },
];

function readStoredLayout() {
  try {
    const v = localStorage.getItem(LAYOUT_KEY);
    if (v === 'table' || v === 'cards') return v;
  } catch {
    /* ignore */
  }
  return 'cards';
}

function formatUpdated(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function statusBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'draft') return 'text-bg-warning text-dark';
  if (s === 'submitted') return 'text-bg-success';
  return 'text-bg-secondary';
}

function formatStatusLabel(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'draft') return 'Draft';
  if (s === 'submitted') return 'Submitted';
  return String(status || '—');
}

function RequirementCards({ items }) {
  if (!items.length) return null;
  return (
    <div className="row g-3">
      {items.map((row) => (
        <div key={row.id} className="col-12 col-md-6 col-xl-4">
          <div className="card h-100 ile-buyer-req-card shadow-sm">
            <div className="card-body d-flex flex-column">
              <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                <h2 className="h6 card-title mb-0">{row.title}</h2>
                <span className={`badge ${statusBadgeClass(row.status)} small`}>{formatStatusLabel(row.status)}</span>
              </div>
              <p className="small text-muted mb-2">{formatUpdated(row.updatedAt)}</p>
              <ul className="list-unstyled small mb-3 flex-grow-1">
                <li>
                  <span className="text-muted">Asset type:</span> {row.assetTypeLabel}
                </li>
                <li>
                  <span className="text-muted">Location:</span> {row.locationLabel}
                </li>
                <li>
                  <span className="text-muted">Budget:</span> {row.budgetLabel}
                </li>
              </ul>
              <div className="d-flex flex-wrap gap-2 mt-auto">
                {String(row.status).toLowerCase() === 'draft' ? (
                  <Link to="/infrastructure-leasing/post-requirement" className="btn btn-sm btn-primary">
                    <FaEdit className="me-1" aria-hidden />
                    Edit draft
                  </Link>
                ) : (
                  <Link
                    to={`/infrastructure-leasing/my-requirements/${row.id}`}
                    className="btn btn-sm btn-outline-primary"
                  >
                    <FaEye className="me-1" aria-hidden />
                    View
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function RequirementTable({ items }) {
  if (!items.length) return null;
  return (
    <div className="table-responsive rounded border ile-buyer-req-table-wrap bg-white">
      <table className="table table-hover table-sm align-middle mb-0 ile-buyer-req-table">
        <thead className="table-light">
          <tr>
            <th scope="col">Title</th>
            <th scope="col">Asset type</th>
            <th scope="col">Location</th>
            <th scope="col">Budget</th>
            <th scope="col">Status</th>
            <th scope="col" className="text-end">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.id}>
              <td className="fw-medium">{row.title}</td>
              <td>{row.assetTypeLabel}</td>
              <td className="small text-muted">{row.locationLabel}</td>
              <td className="small">{row.budgetLabel}</td>
              <td>
                <span className={`badge ${statusBadgeClass(row.status)}`}>{formatStatusLabel(row.status)}</span>
              </td>
              <td className="text-end text-nowrap">
                {String(row.status).toLowerCase() === 'draft' ? (
                  <Link to="/infrastructure-leasing/post-requirement" className="btn btn-sm btn-primary">
                    Edit
                  </Link>
                ) : (
                  <Link to={`/infrastructure-leasing/my-requirements/${row.id}`} className="btn btn-sm btn-outline-primary">
                    View
                  </Link>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function InfrastructureLeasingBuyerRequirementsPage() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [viewMode, setViewMode] = useState(readStoredLayout);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAssetClass, setFilterAssetClass] = useState('all');
  const [filterLocation, setFilterLocation] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchMyInfrastructureRequirementsAPI();
      if (res?.success === false) {
        toast.error(res?.message || 'Could not load requirements.');
        setItems([]);
        return;
      }
      setItems(Array.isArray(res?.data?.items) ? res.data.items : []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not load requirements.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const persistLayout = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(LAYOUT_KEY, mode);
    } catch {
      /* ignore */
    }
  };

  const filtered = useMemo(() => {
    const q = filterLocation.trim().toLowerCase();
    return items.filter((row) => {
      if (filterStatus !== 'all' && String(row.status).toLowerCase() !== filterStatus) {
        return false;
      }
      if (filterAssetClass !== 'all' && String(row.assetClass) !== filterAssetClass) {
        return false;
      }
      if (q) {
        const blob = `${row.title} ${row.locationLabel} ${row.assetTypeLabel}`.toLowerCase();
        if (!blob.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [items, filterStatus, filterAssetClass, filterLocation]);

  return (
    <div className="container-fluid py-4 ile-buyer-req">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <div>
          <Link to="/dashboard/buyer" className="btn btn-link btn-sm text-decoration-none ps-0 mb-1">
            <FaChevronLeft className="me-1" aria-hidden />
            Buyer dashboard
          </Link>
          <h1 className="h4 mb-0">My requirements</h1>
          <p className="text-muted small mb-0">Infrastructure leasing — each item shows its status (Draft or Submitted).</p>
        </div>
        <Link to="/infrastructure-leasing/post-requirement?new=1" className="btn btn-primary btn-sm">
          Post new requirement
        </Link>
      </div>

      <div className="ile-buyer-req-toolbar mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-auto">
            <span className="small text-muted d-block mb-1">View</span>
            <div className="btn-group" role="group" aria-label="Layout">
              <button
                type="button"
                className={`btn btn-sm btn-outline-secondary${viewMode === 'cards' ? ' active' : ''}`}
                onClick={() => persistLayout('cards')}
              >
                <FaTh className="me-1" aria-hidden />
                Cards
              </button>
              <button
                type="button"
                className={`btn btn-sm btn-outline-secondary${viewMode === 'table' ? ' active' : ''}`}
                onClick={() => persistLayout('table')}
              >
                <FaTable className="me-1" aria-hidden />
                Table
              </button>
            </div>
          </div>
          <div className="col-6 col-md-2">
            <label className="small text-muted d-block mb-1" htmlFor="ile-req-filter-status">
              Status
            </label>
            <select
              id="ile-req-filter-status"
              className="form-select form-select-sm"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
            </select>
          </div>
          <div className="col-6 col-md-3">
            <label className="small text-muted d-block mb-1" htmlFor="ile-req-filter-asset">
              Asset type
            </label>
            <select
              id="ile-req-filter-asset"
              className="form-select form-select-sm"
              value={filterAssetClass}
              onChange={(e) => setFilterAssetClass(e.target.value)}
            >
              <option value="all">All types</option>
              {ASSET_CLASS_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md">
            <label className="small text-muted d-block mb-1" htmlFor="ile-req-filter-loc">
              Location / search
            </label>
            <input
              id="ile-req-filter-loc"
              type="search"
              className="form-control form-control-sm"
              placeholder="Filter by title or location…"
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-muted">Loading…</p>
      ) : items.length === 0 ? (
        <div className="border rounded p-5 text-center text-muted bg-white">
          <p className="mb-2">No requirements found.</p>
          <p className="small mb-3">Post a requirement to see it here as a draft or after you submit.</p>
          <Link to="/infrastructure-leasing/post-requirement?new=1" className="btn btn-primary btn-sm">
            Post your requirement
          </Link>
        </div>
      ) : filtered.length === 0 ? (
        <div className="border rounded p-5 text-center text-muted bg-white">
          No requirements match your filters.{' '}
          <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => {
            setFilterStatus('all');
            setFilterAssetClass('all');
            setFilterLocation('');
          }}>
            Clear filters
          </button>
        </div>
      ) : viewMode === 'cards' ? (
        <RequirementCards items={filtered} />
      ) : (
        <RequirementTable items={filtered} />
      )}
    </div>
  );
}
